/*
FindoutAuth
findoutのみでの認証方式
*/

import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// third-party
import 'firebase/compat/auth';
import { useCookies } from 'react-cookie';
import jwtDecode from 'jwt-decode';

// project imports
import accountReducer from 'store/accountReducer';
import { LOGOUT, LOGIN } from 'store/actions';
import Loader from 'ui-component/Loader';
import { verifyFoIdtoken, refreshIdtoken, revokeIdtoken } from 'utils/function/auth';

// firebase package imports
import { updateProfile } from 'firebase/auth';

const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  redirectFailed: false,
  user: null,
  userProfile: '',
  url: '',
  foIdtoken: ''
};

// リファラ情報を取得
const referrer = window.document.referrer;
// console.log('reffer', referrer);

// チャンネルID,遷移前のURL取得
// console.log('window.location.href', window.location.href);
const url = new URL(window.location.href);
// console.log('new Url', url);

// liff用のstateパラメータ置換
let search = decodeURIComponent(url.search).replace('?liff.state=', '');
search = decodeURIComponent(search).replace('#state', '');

// console.log('FirebaseContext search', search);

const query = new URLSearchParams(search);
// console.log('FirebaseContext query', query);

const foIdtoken = query.get('foidtk');
const foIdtokenRefresh = query.get('foidtk_refresh');

// const qProviderGroup = query.get('provider_group'); // プロバイダグループ
// const qProviderId = query.get('provider_id'); // 認証要求元のプロバイダユーザID
// const qType = query.get('provider_type');
// const qId = query.get('provider_userid'); // 認証要求元のプロバイダユーザID
// const qRedirectUri = query.get('redirect_uri'); // 認証要求元のリダイレクトUri

const FindoutContext = createContext(null);

export const FindoutProvider = ({ children }) => {
  // console.log('start FindoutProvider');
  const [state, dispatch] = useReducer(accountReducer, initialState);
  const navigate = useNavigate();
  const [cookies, setCookie, removeCookie] = useCookies();
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    // (() => {
    const verifyTokenAndUpdateState = async () => {
      try {
        // console.log('start useEffect');
        if (foIdtoken || cookies.foidtk) {
          const headers = {
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
            Authorization: `Bearer ${foIdtoken ?? cookies.foidtk}`
            // withCredentials: true
          };

          // Idtoken verify
          await verifyFoIdtoken(headers).then((res) => {
            // console.log('res varifyFoIdToken', res);
            if (res?.data?.uid) {
              // console.log('verifyFoIdtoken setCookie前');
              setCookie('foidtk', foIdtoken ?? cookies.foidtk, {
                path: '/', // クッキーが有効なパス
                maxAge: 3600, // 有効期限（秒単位: 1時間）
                secure: true, // HTTPS通信でのみ送信
                sameSite: 'strict' // SameSite属性（strict/lax/none）
              });
              setCookie('foidtk_refresh', foIdtokenRefresh ?? cookies.foidtk_refresh, {
                path: '/', // クッキーが有効なパス
                maxAge: 2592000, // 有効期限（秒単位: 30日間）
                secure: true, // HTTPS通信でのみ送信
                sameSite: 'strict' // SameSite属性（strict/lax/none）
              });

              dispatch({
                type: LOGIN,
                payload: {
                  userProfile: res.data,
                  url,
                  tokens: {
                    foIdtoken: foIdtoken ?? cookies.foidtk,
                    foIdtokenRefresh: foIdtokenRefresh ?? cookies.foidtk_refresh,
                    jwt: process.env.REACT_APP_JWT_TOKEN
                  }
                }
              });
            } else if (!state.isLoggedIn) {
              // console.log('dispatch logOut前');
              dispatch({
                type: LOGOUT,
                payload: {
                  url
                }
              });
              removeCookie('foidtk', { path: '/' });
              removeCookie('foidtk_refresh', { path: '/' });
            }
          });
        } else {
          // console.log('トークン情報なし');
          // await logout();
          // トークン情報がない場合
          dispatch({
            type: LOGOUT,
            payload: {
              url
            }
          });
          removeCookie('foidtk', { path: '/' });
          removeCookie('foidtk_refresh', { path: '/' });
          window.location.replace(`${process.env.REACT_APP_LOGOUT_CALLBACK}?status=401&referer=${window.location.origin}`);
        }
        // console.log('end useEffect');
      } catch (error) {
        // エラーハンドリング
        // console.log('ログイン処理中にエラー発生');
      }
    };
    verifyTokenAndUpdateState();
    // })();
  }, [dispatch]);

  const logout = async () => {
    // console.log('start logout');
    const headers = {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      Authorization: `Bearer ${foIdtoken ?? cookies.foidtk}`
    };
    const data = {
      foRefToken: state.tokens?.foIdtokenRefresh
    };
    await revokeIdtoken(headers, data);
    removeCookie('foidtk', { path: '/' });
    removeCookie('foidtk_refresh', { path: '/' });
    window.location.replace(`${process.env.REACT_APP_LOGOUT_CALLBACK}?status=401&referer=${window.location.origin}`);
  };

  const handleSessionTimeout = () => {
    // console.log('start handleSessionTimeout');
    logout();
    // console.log('end handleSessionTimeout');
  };

  const checkTokenExpiration = async () => {
    // console.log('start checkTokenExpiration');
    if (!cookies.foidtk && !foIdtoken) {
      console.error('Invalid or missing token.');
      handleSessionTimeout(); // トークンが無効の場合の処理
      return;
    }
    const foidtk = foIdtoken ?? cookies.foidtk;

    const decoded = jwtDecode(foidtk);
    const currentTime = Date.now() / 1000; // 秒単位
    const expiresIn = decoded.exp - currentTime;

    // console.log('expiresIn', expiresIn);
    // 残り1分未満でリフレッシュ
    if (expiresIn < 60 && !isRefreshing) {
      // console.log('start refreshIdtoken');
      setIsRefreshing(true);
      try {
        const headers = {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '',
          Authorization: `Bearer ${foidtk}`
        };
        const authData = {
          foRefToken: cookies.foidtk_refresh ?? foIdtokenRefresh
        };

        const res = await refreshIdtoken(headers, authData);
        // console.log('res refreshIdtoken', res);
        if (res.status === 200) {
          // cookie再セット
          setCookie('foidtk', res.data.idToken, {
            path: '/', // クッキーが有効なパス
            maxAge: 3600, // 有効期限（秒単位: 1時間）
            secure: true, // HTTPS通信でのみ送信
            sameSite: 'strict' // SameSite属性（strict/lax/none）
          });
          setCookie('foidtk_refresh', res.data.refreshToken, {
            path: '/', // クッキーが有効なパス
            maxAge: 2592000, // 有効期限（秒単位: 30日間）
            secure: true, // HTTPS通信でのみ送信
            sameSite: 'strict' // SameSite属性（strict/lax/none）
          });
        } else {
          // リフレッシュトークン有効期限切れ
          // console.log('リフレッシュトークン有効期限切れ');
          handleSessionTimeout();
        }
      } catch (error) {
        console.error('トークンリフレッシュ中にエラーが発生:', error);
        handleSessionTimeout();
      } finally {
        setIsRefreshing(false);
      }
    } else if (expiresIn <= 0) {
      // 有効期限切れ
      // console.log('トークン有効期限切れ');
      handleSessionTimeout();
    }
    // console.log('end checkTokenExpiration');
  };

  const repeatCheckTokenExpiration = async () => {
    // console.log('start repeatCheckToken');
    if (!isRefreshing) {
      await checkTokenExpiration(); // トークン確認処理を呼び出す
    } else {
      // console.log('現在処理中のため、トークン確認はスキップされます');
    }

    // 10秒後に再度繰り返す
    setTimeout(repeatCheckTokenExpiration, 10000); // 10秒後に再帰的に呼び出し
    // console.log('end repeatCheckToken');
  };

  // トークン有効期限確認用のタイマー関数処理
  useEffect(() => {
    // console.log('start useEffect repeatCheckTokenExpiration');
    repeatCheckTokenExpiration(); // 初回呼び出し
    // console.log('end useEffect repeatCheckTokenExpiration');
  }, []);

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }
  // console.log('end FindoutAuthProvider');

  return (
    <FindoutContext.Provider
      value={{
        ...state,
        url,
        dispatch,
        login: () => {},
        logout,
        updateProfile
      }}
    >
      {children}
    </FindoutContext.Provider>
  );
};
FindoutProvider.propTypes = {
  children: PropTypes.node
};

export default FindoutContext;
